import { find } from '@execonline-inc/collections';
import { toResult } from '@execonline-inc/maybe-adapter';
import {
  Link as LinkR,
  Linkable as LinkableR,
  PossiblyLinkable as PossiblyLinkableR,
  Resource as ResourceR,
  ResourceWithErrors as ResourceWithErrorsR,
  ResourceWithMetadata as ResourceWithMetadataR,
  links as linksR,
  payload as payloadR,
  resource as resourceR,
} from '@execonline-inc/resource';
import { Maybe } from 'maybeasy';
import { Result } from 'resulty';

const rels = [
  'all-experiences',
  'communication-overrides-index',
  'communication-preview',
  'upsert-communication-override',
  'destroy-communication-override',
  'communication-inherited-template',
  'communication-disable',
  'communication-disables-bulk',
  'view',
  'sprite',
  'overview_video',
  'app-session',
  'asset',
  'avatar',
  'bulk-provision-license',
  'bulk-assign-revoke-license',
  'bulk-reclaim-license',
  'details',
  'create',
  'create-browse-all-learning-design',
  'create-byo-learning-design',
  'create-learning-collection-template-v2',
  'create-learning-design',
  'current-user',
  'program-deep-link-import',
  'learning-collection-deep-links',
  'dashboard',
  'deactivate',
  'delete',
  'download-overview-pdf',
  'edit',
  'edit-learning-collection-template',
  'edit-learning-collection-template-v2',
  'edit-user-group',
  'coaching-portal-app',
  'enterprise-portal-app',
  'experiences',
  'generate',
  'edit-learning-collection',
  'hide-learning-collection',
  'import-participants',
  'import-participants-options',
  'import-multiple-participants',
  'invite',
  'learning-collection',
  'learning-collection-invitations',
  'manage-collaborator-access',
  'learning-collections',
  'learning-design',
  'learning-designs',
  'learning-platform-app',
  'license-allocations',
  'logo-on-white',
  'logout',
  'messages',
  'new',
  'new-learning-collection',
  'new-learning-collection-template-v2',
  'new-product-collection-template-v2',
  'new-user-group-memberships',
  'options',
  'organization',
  'p3-profile-edit',
  'parent',
  'participant',
  'participants',
  'people-v2',
  'preview-learning-collection',
  'preview-learning-collection-template',
  'preview-learning-collection-template-v2',
  'chat-metadata',
  'update-chat-metadata',
  'privacy',
  'update-product-license',
  'key-metrics',
  'product-licenses',
  'product-licenses-lima',
  'product-license-export',
  'popular-competencies',
  'products',
  'profile',
  'program-deep-link',
  'publish',
  'quicksight-visualizations',
  'reactivate',
  'report',
  'report-template',
  'reports',
  'self',
  'shared-open-enrollment-short-link',
  'shared-url-short-link',
  'share-urls-options',
  'support',
  'terms_and_conditions',
  'thumbnail',
  'update',
  'update-learning-collection-template-v2',
  'user-group',
  'user-groups',
  'user-groups-new',
  'tasks',
  'license-insights',
  'recently-logged-in',
  'recent-activities',
  'direct-upload',
  'logo',
  'section-type-options',
  'learning-collection-logo',
  'create-learning-collection-v2',
  'competencies',
  'learning-collections-index',
  'create-learning-collection-v2-ai',
  'overview_video',
  'adaptive',
  'v2-participant-options',
  'v2-participant-email-suggestions',
  'v2-participant-group-suggestions',
  'v2-participant-directory',
  'v2-create-participant',
  'license-options',
  'adminstudio-share-learning-collections-learning-collection',
  'adminstudio-share-learning-collections-participants-directory',
  'adminstudio-share-learning-collections-participants-search-options',
  'adminstudio-share-learning-collections-search-users',
  'adminstudio-share-learning-collections-search-user-groups',
  'adminstudio-share-learning-collections-license-alerts',
  'adminstudio-share-learning-collections-share',
  'adminstudio-program-family-share-urls',
  'filter_options',
  'impact-report-summaries',
  'action-plan-report',
  'sso-configurations',
  'sso-configuration',
  'assign-domain',
  'submit-metadata',
  'test-sign-in',
  'update-attribute-mapping',
  // DVLP-7601 — admin preview
  'preview-learning-collection-v2',
  'preview-license-options',
  'preview-learning-collection-payload-v2',
] as const;

export type Rel = (typeof rels)[number];

export const toRel = (value: string): Result<string, Rel> =>
  toResult(
    `Expected to find an HTTP rel string. Instead I found ${value}`,
    find((rel) => rel === value, rels),
  );

export type Link = LinkR<Rel>;
export type Linkable = LinkableR<Rel>;
export type PossiblyLinkable = PossiblyLinkableR<Rel>;

export type Resource<T> = ResourceR<T, Rel>;
export type ResourceWithErrors<T> = ResourceWithErrorsR<T, Rel>;
export type ResourceWithMetadata<T, M> = ResourceWithMetadataR<T, M, Rel>;
export type ResourceWithActions<T, A> = Resource<T> & { actions: A };

export interface ResourceWithEmbeddedR<T, E, Rel extends string> extends ResourceR<T, Rel> {
  embedded: E;
}

export const resourceWithEmbeddedR: <Rel extends string, T, E>(
  links: readonly LinkR<Rel>[],
  payload: T,
  embedded: E,
) => ResourceWithEmbeddedR<T, E, Rel> = (links, payload, embedded) => ({
  links,
  payload,
  embedded,
});

export const resource: <T>(links: ReadonlyArray<Link>, payload: T) => Resource<T> = resourceR;

export const payload: <A, R extends Resource<A>>(r: R) => A = payloadR;

export type ValidationErrors = ValidationError[];

export interface ValidationError {
  kind: 'validation-error';
  title: 'Invalid Attribute';
  resource: string;
  attributePath: string;
  attribute: string;
  detail: string;
  error: string;
}

export type { IdentifiablePayload, ServerError } from '@execonline-inc/resource';

export type Embedded<T> = {
  [K in Rel & keyof T]: Maybe<Resource<T[K]>>;
};

export type ResourceWithEmbedded<T, E> = Resource<T> & { embedded: Embedded<E> };

export const links = linksR<Rel, Linkable>;
